import React, {useState, useEffect, useCallback} from "react"
import {formatTextTestIds, formatPrice} from "@next/utils"
import {UxCTA as CTA} from "@next/ux-cta"
import {
    List,
    Title,
    ProductDetailsContainer,
    ButtonContainer,
    ListContainer,
    SearchTitle,
    StyledSvg,
    Price,
    ButtonContainerWrapper,
} from "./components"
import {Container} from "../../components"
import {AutoCompleteProducts} from "../../../../models/autocomplete"
import {EnrichedMaxItemsConfig} from "../../../../models/search"
import urls from "../../../../config/urls"
import {
    SEARCH_DATA_GA,
    HEADER_AUTOCOMPLETE_IMAGE_DATA_GA,
    HEADER_AUTOCOMPLETE_SEE_ALL_RESULTS_DATA_GA,
    PRODUCT_WIDTH,
    IMAGE_GAP,
    STARTING_PADDING,
} from "../../../../config/constants"
import {removeDeptFromSessionStorage} from "../../../../utils/removeDeptFromSessionStorage"
import {configRelativePathURL} from "../../../../utils/configUrlPath"
import getImagePath from "../../../../utils/imagePath"
import {formatCdnPathWithVariant} from "../../../../utils/getCdnUrl"

const {
    autoComplete: {
        images: {AltItemZoomDir, AltItemThumbDir, newDirectoryName},
    },
} = urls

export type AutocompleteProductsProps = {
    products: AutoCompleteProducts[]
    term: string | any
    maxItems: EnrichedMaxItemsConfig
    handleSuggestionClick: (suggestion: string, siteUrl: string) => void
    text: any
    currencyCode: string
    locale: string
    siteUrl: string
    realm: string
    threeFourImageryEnabled: boolean
    enrichedSearchImagePath: string
    isTPClient: boolean
    cleanSiteUrl: string
    isTpSuperLite: boolean
    variant: string
    iconsVersionPath: string
    isRTL: boolean
    xxlBreakpointEnabled: boolean
}

export const AutocompleteProducts = ({
    products,
    term,
    handleSuggestionClick,
    maxItems,
    text,
    siteUrl,
    currencyCode,
    locale,
    realm,
    threeFourImageryEnabled,
    enrichedSearchImagePath,
    isTPClient,
    variant,
    iconsVersionPath,
    isRTL,
    xxlBreakpointEnabled,
    cleanSiteUrl,
    isTpSuperLite,
}: AutocompleteProductsProps) => {
    const [maxItemscount, setMaxItensCount] = useState(0)
    const calculateMaxItems = useCallback(() => {
        const productContainer = document.getElementById("productContainer")
        if (!productContainer) return 0

        const {width: contentWidth} = productContainer.getBoundingClientRect()

        const availableSpace = contentWidth - STARTING_PADDING
        const requiredWidth = PRODUCT_WIDTH + IMAGE_GAP
        const maxShowableItems = Math.floor((availableSpace + IMAGE_GAP) / requiredWidth)

        setMaxItensCount(maxShowableItems)
        return maxShowableItems
    }, [])

    useEffect(() => {
        calculateMaxItems()

        const handleResize = () => calculateMaxItems()
        window.addEventListener("resize", handleResize)

        return () => window.removeEventListener("resize", handleResize)
    }, [calculateMaxItems])
    return (
        <Container id="productContainer">
            {products?.length > 0 && (
                <>
                    <SearchTitle data-testid="header-autocomplete-products-title" variant="subtitle2">
                        {text.termTitle}
                        &quot;{term}&quot;
                    </SearchTitle>
                    <ListContainer
                        data-testid="searchBar-products"
                        maxitems={maxItems}
                        xxlBreakpointEnabled={xxlBreakpointEnabled}
                        maxItemscount={maxItemscount}
                        productLength={products.length}
                    >
                        {products.map((product: AutoCompleteProducts) => {
                            const {url, title, tpsearchdescription, pid, sale_price, thumb_image} = product

                            const displayTitle =
                                isTPClient && tpsearchdescription !== undefined && tpsearchdescription !== ""
                                    ? tpsearchdescription
                                    : title

                            const newImagePath = threeFourImageryEnabled
                                ? getImagePath(enrichedSearchImagePath, pid, "450")
                                : thumb_image
                                      .replace(AltItemZoomDir, newDirectoryName)
                                      .replace(AltItemThumbDir, newDirectoryName)
                            return (
                                <List key={pid} onClick={removeDeptFromSessionStorage}>
                                    <a
                                        data-testid={formatTextTestIds(`header-autocomplete-product-${title}`)}
                                        href={configRelativePathURL(url, siteUrl)}
                                        data-ga-v1={SEARCH_DATA_GA}
                                        data-ga-v2={term}
                                        data-ga-v3={HEADER_AUTOCOMPLETE_IMAGE_DATA_GA}
                                        data-ga-v4={title}
                                    >
                                        <img src={newImagePath} alt={title} data-testid="searchBar-products-image" />
                                        <ProductDetailsContainer>
                                            <Title variant="body2">{displayTitle}</Title>
                                            <Price>
                                                {formatPrice(sale_price, sale_price, currencyCode, locale, realm)}
                                            </Price>
                                        </ProductDetailsContainer>
                                    </a>
                                </List>
                            )
                        })}
                    </ListContainer>
                </>
            )}
            <ButtonContainerWrapper>
                <ButtonContainer>
                    <CTA
                        text={text.searchLinktext}
                        color="secondary"
                        state="enabled"
                        variant="outlined"
                        size="medium"
                        onClick={() => handleSuggestionClick(term, isTpSuperLite ? cleanSiteUrl : siteUrl)}
                        testId="header-autocomplete-product-button"
                        data-ga-v1={SEARCH_DATA_GA}
                        data-ga-v2={term}
                        data-ga-v3={HEADER_AUTOCOMPLETE_SEE_ALL_RESULTS_DATA_GA}
                        endIcon={
                            <StyledSvg
                                isRTL={isRTL}
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <use
                                    className="default"
                                    href={`${formatCdnPathWithVariant(
                                        "chevron-right-m-default.svg",
                                        realm,
                                        variant,
                                        iconsVersionPath,
                                    )}#default`}
                                />
                            </StyledSvg>
                        }
                        className="header-autocomplete-product-button"
                    />
                </ButtonContainer>
            </ButtonContainerWrapper>
        </Container>
    )
}

export default AutocompleteProducts
